import classNames from 'classnames';

import React, { forwardRef, ForwardRefExoticComponent, HTMLAttributes, RefAttributes } from 'react';

import styles from './control.scss';
import type { Type } from './types';

type ControlProps = RefAttributes<HTMLDivElement & HTMLInputElement> &
  HTMLAttributes<HTMLDivElement> & {
    type?: Type;
    name?: string;
    value?: string;
    checked?: boolean;
    disabled?: boolean;
    dataTestId?: string;
    onChange: (value: string | boolean, name: string) => void;
  };

const COMPONENT_ID = 'modulor-control';

export const Control: ForwardRefExoticComponent<ControlProps> = forwardRef<
  HTMLDivElement & HTMLInputElement,
  ControlProps
>(
  (
    {
      type = 'checkbox',
      name = '',
      value = '',
      checked = false,
      disabled = false,
      onChange,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (typeof onChange === 'function') {
        onChange(e.target.checked, name);
      }
    };

    return (
      <div
        className={classNames(className, styles.control)}
        ref={ref}
        data-id={dataTestId}
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}>
        <label
          data-testid={`${dataTestId}-label`}
          className={classNames({ [styles.container]: type !== 'switch' })}>
          <input
            name={name}
            type="checkbox"
            className={classNames(
              { [styles.switch]: type === 'switch' },
              { [styles['input-mode']]: disabled && checked },
            )}
            value={value}
            disabled={disabled}
            checked={checked}
            onChange={handleChange}
            data-testid={`${dataTestId}-input`}
            {...rest}
          />
        </label>
      </div>
    );
  },
);

Control.displayName = 'Control';
